/**
 * 広告と課金の窓口。
 *
 * ゲーム側はこのインターフェースだけを見る。中身は動いている場所で差し替える。
 *
 * | 動いている場所 | 中身 | 広告・アイテム・課金 |
 * |---|---|---|
 * | Web版（本番） | WebPlatform | **出さない**（Web版に広告を入れない、の原則） |
 * | staging / 開発で `?platform=mock` | MockPlatform | 出す。広告は数秒の仮画面、購入はお金が動かない |
 * | アプリ iOS（Capacitor） | NativePlatform | AdMob の広告 ＋ App Store の課金 |
 * | アプリ Android（Capacitor） | NativePlatform | AdMob の広告だけ。**課金なし**（Google Play で課金すると住所が公開されるため） |
 */

import type { ProductId } from './products';

/**
 * 購入の結果。ショップで「失敗」と「やめた」を分けて知らせるため。
 * - purchased：成立
 * - cancelled：ユーザーが自分でやめた（何も言わない）
 * - pending：保護者の承認待ちなど（承認されれば次の起動で届く）
 * - failed：ストアにつながらない・エラー
 */
export type PurchaseOutcome = 'purchased' | 'cancelled' | 'pending' | 'failed';

/** 端末の中で予約するお知らせ（今日の県）。外へは何も送らない。 */
export interface LocalNotice {
  /** 予約の番号（同じ番号で入れ替わる）。 */
  id: number;
  title: string;
  body: string;
  at: Date;
  /** タップしたときに開く場所（例: /fukuoka?daily=2026-10-05）。 */
  href: string;
}

/** リワード広告を出す場所。計測で分けて見られるように名前を付けておく。 */
/** rescue＝「こぼれそう！」のときに盤面のそばに出す「広告でおはし」（2026-10-11）。 */
export type RewardedPlacement = 'continue' | 'item' | 'rest' | 'rescue';

/** iOS のトラッキング許可（ATT）の状態。AdMob の trackingAuthorizationStatus と同じ名前。 */
export type TrackingStatus = 'authorized' | 'denied' | 'restricted' | 'notDetermined';

/**
 * askTrackingPermission の結果。計測（att）に残すため。
 * - status：聞いたあとの状態（ダイアログが時間切れなら notDetermined のまま）
 * - prompted：この呼び出しで OS のダイアログを出したか（出したのは1台に1回だけ）
 */
export interface TrackingResult {
  status: TrackingStatus;
  prompted: boolean;
}

export interface Platform {
  readonly kind: 'web' | 'mock' | 'native';
  /** 広告・アイテム・課金の画面を出すか。Web版は false。 */
  readonly monetized: boolean;
  /** お金で買う商品（おはし・買い切り）を出すか。Android 版は広告だけなので false。 */
  readonly canPurchase: boolean;
  /** 起動時の準備（同意の確認・広告の初期化・値段の取得）。何度呼んでもよい。 */
  init(): Promise<void>;
  /**
   * iOS のトラッキング許可（ATT）を、まだ聞いていなければ聞く。何度呼んでもよい。
   * 起動直後に呼ぶ（AssistController.startup）。アプリが前面に出てから少し待って聞き、
   * 広告SDKの初期化はその返事を待ってから行う（2026-10-20 審査の差し戻し Guideline 2.1 を受けて）。
   */
  askTrackingPermission(): Promise<TrackingResult | null>;
  /** いま持っている買い切り商品。**ストアのログインを求めない**（起動時に黙って確かめる用）。 */
  ownedProducts(): Promise<ProductId[]>;
  /** リワード広告。最後まで見たら true。 */
  showRewarded(placement: RewardedPlacement): Promise<boolean>;
  /**
   * ゲームの合間の広告（見終わるか閉じたら resolve）。
   * 出せなかった（読み込めていなかった）ときは false を返す。Web版・モックは何も返さない（出したものとして扱う）。
   */
  showInterstitial(): Promise<boolean | void>;
  /** 広告の診断（テスト用ビルドのアプリだけ。ほかは無いか null）。 */
  adDiagnostics?(): string[] | null;
  /** 購入。結果を返す（例外は投げない）。 */
  purchase(productId: ProductId): Promise<PurchaseOutcome>;
  /** 買い切りの復元。持っている非消耗型の商品を返す。ストアにつながらないときは例外を投げる。 */
  restorePurchases(): Promise<ProductId[]>;
  /** 表示用の値段（取れなければ null）。 */
  priceOf(productId: ProductId): string | null;
  /**
   * 値段が取れていなければ、ストアにもう一度問い合わせる（ショップを開いたとき用）。
   * 取得中にもう一度呼ばれたら、同じ問い合わせの終わりを待つだけ（二重に走らない）。
   * 全部の値段がそろっていれば true。
   */
  refreshPrices(): Promise<boolean>;
  /**
   * ストアのレビュー依頼（iOS の SKStoreReview）。実際に出すかどうかは OS が決める（年3回まで）。
   * iOS アプリだけ。Web・モック・Android は何もしない。
   */
  requestReview(): Promise<void>;
  /** 端末内のお知らせ（今日の県）を使えるか。iOS アプリだけ true。 */
  readonly canNotify: boolean;
  /** お知らせの許可を OS に聞く（まだなら）。許可されていれば true。 */
  requestNotificationPermission(): Promise<boolean>;
  /** 予約を入れ替える（前の予約は消す）。許可がなければ何もしない。 */
  scheduleNotices(list: readonly LocalNotice[]): Promise<void>;
  /** 予約をすべて消す。 */
  cancelNotices(): Promise<void>;
  /** お知らせをタップしてアプリが開いたとき。 */
  onNoticeOpened(handler: (href: string) => void): void;
}

/** Web版：何も出さない。 */
export class WebPlatform implements Platform {
  readonly kind = 'web' as const;
  readonly monetized = false;
  readonly canPurchase = false;
  async init(): Promise<void> {}
  async askTrackingPermission(): Promise<TrackingResult | null> {
    return null;
  }
  async ownedProducts(): Promise<ProductId[]> {
    return [];
  }
  async showRewarded(): Promise<boolean> {
    return false;
  }
  async showInterstitial(): Promise<void> {}
  async purchase(): Promise<PurchaseOutcome> {
    return 'failed';
  }
  async restorePurchases(): Promise<ProductId[]> {
    return [];
  }
  priceOf(): string | null {
    return null;
  }
  async refreshPrices(): Promise<boolean> {
    return false;
  }
  async requestReview(): Promise<void> {}
  readonly canNotify = false;
  async requestNotificationPermission(): Promise<boolean> {
    return false;
  }
  async scheduleNotices(): Promise<void> {}
  async cancelNotices(): Promise<void> {}
  onNoticeOpened(): void {}
}
